import type { ItemProps } from "@bedrock/domain";

type ArrowProps = Extract<ItemProps, { kind: "arrow" }>;
type FrameProps = Extract<ItemProps, { kind: "frame" }>;

interface ArrowGeometry {
  path: string;
  tip: { x: number; y: number };
  angle: number;
}

export function arrowGeometry(width: number, height: number, path: ArrowProps["path"]): ArrowGeometry {
  const tip = path === "straight"
    ? { x: width - 13, y: height / 2 }
    : { x: width - 13, y: Math.max(17, height * .22) };
  if (path === "straight") {
    return { path: `M 13 ${tip.y} L ${tip.x} ${tip.y}`, tip, angle: 0 };
  }
  const startY = Math.min(height - 17, height * .78);
  const controlTwo = { x: width * .68, y: height * .22 };
  return {
    path: `M 13 ${startY} C ${width * .32} ${startY}, ${controlTwo.x} ${controlTwo.y}, ${tip.x} ${tip.y}`,
    tip,
    angle: Math.atan2(tip.y - controlTwo.y, tip.x - controlTwo.x)
  };
}

export function arrowHeadPoints(geometry: ArrowGeometry): string {
  const length = 17;
  const halfWidth = 8;
  const cosine = Math.cos(geometry.angle);
  const sine = Math.sin(geometry.angle);
  const baseX = geometry.tip.x - length * cosine;
  const baseY = geometry.tip.y - length * sine;
  const first = { x: baseX + halfWidth * sine, y: baseY - halfWidth * cosine };
  const second = { x: baseX - halfWidth * sine, y: baseY + halfWidth * cosine };
  return `${geometry.tip.x},${geometry.tip.y} ${first.x},${first.y} ${second.x},${second.y}`;
}

export function CanvasArrow({ props, width, height }: { props: ArrowProps; width: number; height: number }) {
  const geometry = arrowGeometry(width, height, props.path);
  const dashArray = props.stroke === "dashed" ? "14 10" : props.stroke === "dotted" ? "1 10" : undefined;
  return <svg
    className={`item-arrow item-arrow--${props.color}`}
    viewBox={`0 0 ${width} ${height}`}
    preserveAspectRatio="none"
    role="img"
    aria-label={`${props.path} ${props.stroke} arrow`}
  >
    <path
      className="item-arrow__line"
      d={geometry.path}
      pathLength={Math.max(width, height)}
      strokeDasharray={dashArray}
      strokeLinecap={props.stroke === "dotted" ? "round" : "square"}
      vectorEffect="non-scaling-stroke"
    />
    {props.head !== "none" && <polygon
      className={`item-arrow__head item-arrow__head--${props.head}`}
      points={arrowHeadPoints(geometry)}
      vectorEffect="non-scaling-stroke"
    />}
  </svg>;
}

export function CanvasFrame({ props }: { props: FrameProps }) {
  const backgroundStyle = props.backgroundStyle ?? "soft";
  const borderStyle = props.borderStyle ?? "solid";
  const borderColor = props.borderColor ?? "match";
  return <div
    className={`item-frame item-frame--${props.color} item-frame--background-${backgroundStyle} item-frame--border-${borderStyle} item-frame--border-color-${borderColor}`}
    role="group"
    aria-label={props.title || "Untitled frame"}
  />;
}

export function CanvasFrameLabel({ props }: { props: FrameProps }) {
  return <div className="item-frame__label"><span>{props.title || "Untitled frame"}</span></div>;
}
